import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { UserPlus, Users, Shield, Ban, RotateCcw, Trash2, Mail, UsersRound, History } from "lucide-react";
import {
  getMySubAccounts, blockSubAccount, unblockSubAccount, removeSubAccount, resendSubAccountInvite,
} from "../../services/subAccountService";
import Card from "../../components/common/Card";
import Badge from "../../components/common/Badge";
import Button from "../../components/common/Button";
import Loader from "../../components/common/Loader";
import EmptyState from "../../components/common/EmptyState";
import ConfirmModal from "../../components/common/ConfirmModal";
import Tabs from "../../components/common/Tabs";
import InviteSubAccountModal from "../../components/sub-accounts/InviteSubAccountModal";
import EditSubAccountPermissionsModal from "../../components/sub-accounts/EditSubAccountPermissionsModal";
import SubAccountActivityLog from "../../components/sub-accounts/SubAccountActivityLog";
import { useAuth } from "../../context/AuthContext";
import { color, radius } from "../../styles/theme";

const STATUS_VARIANT = { Active: "success", Pending: "warning", Blocked: "danger", Deactivated: "neutral", Expired: "neutral" };

const formatDate = (value) => {
  if (!value) return "";
  return new Date(value).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

/**
 * Sub-Accounts management — Promoter Panel spec ch.11.2-11.4. Deliberately a
 * separate top-level page rather than a third TeamPage.jsx tab: Sub-Accounts
 * are business-wide (not per-event) and CRUD/management-heavy, unlike
 * TeamPage's read-only cross-event roster.
 */
const SubAccountsPage = () => {
  const { t } = useTranslation(["subAccounts", "common"]);
  const { isSubAccount } = useAuth();
  const tabs = [
    { key: "roster", labelKey: "tabs.roster", icon: UsersRound },
    ...(!isSubAccount ? [{ key: "activityLog", labelKey: "tabs.activityLog", icon: History }] : []),
  ];
  const [tab, setTab] = useState("roster");
  const [subAccounts, setSubAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [editingPermissions, setEditingPermissions] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null); // { type: 'block'|'unblock'|'remove'|'resend', subAccount }
  const [actionBusy, setActionBusy] = useState(false);

  const load = () => {
    setLoading(true);
    return getMySubAccounts()
      .then((res) => setSubAccounts(res.data?.Result || []))
      .catch(() => toast.error(t("loadError")))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runConfirmedAction = async () => {
    if (!confirmAction) return;
    const { type, subAccount } = confirmAction;
    setActionBusy(true);
    try {
      if (type === "block") {
        const res = await blockSubAccount(subAccount.SubAccountId);
        toast.success(res.data?.Message || t("toasts.blockSuccess"));
      } else if (type === "unblock") {
        const res = await unblockSubAccount(subAccount.SubAccountId);
        toast.success(res.data?.Message || t("toasts.unblockSuccess"));
      } else if (type === "remove") {
        const res = await removeSubAccount(subAccount.SubAccountId);
        toast.success(res.data?.Message || t("toasts.removeSuccess"));
      } else if (type === "resend") {
        const res = await resendSubAccountInvite(subAccount.SubAccountId);
        toast.success(res.data?.Message || t("toasts.resendSuccess"));
      }
      setConfirmAction(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.Message || t("toasts.actionFailed"));
    } finally {
      setActionBusy(false);
    }
  };

  const confirmCopy = confirmAction && {
    block: {
      title: t("confirm.blockTitle"),
      message: t("confirm.blockMessage", { name: confirmAction.subAccount.Name }),
      danger: true,
      confirmText: t("actions.block", { ns: "subAccounts" }),
    },
    unblock: {
      title: t("confirm.unblockTitle"),
      message: t("confirm.unblockMessage", { name: confirmAction.subAccount.Name }),
      danger: false,
      confirmText: t("actions.unblock", { ns: "subAccounts" }),
    },
    remove: {
      title: t("confirm.removeTitle"),
      message: t("confirm.removeMessage", { name: confirmAction.subAccount.Name }),
      danger: true,
      confirmText: t("actions.remove", { ns: "subAccounts" }),
    },
    resend: {
      title: t("confirm.resendTitle"),
      message: t("confirm.resendMessage", { email: confirmAction.subAccount.Email }),
      danger: false,
      confirmText: t("actions.resendInvite", { ns: "subAccounts" }),
    },
  }[confirmAction.type];

  return (
    <div>
      <div style={s.headerRow}>
        <div>
          <h2 style={s.heading}>{t("heading")}</h2>
          <p style={s.subheading}>{t("subheading")}</p>
        </div>
        {tab === "roster" && <Button icon={UserPlus} onClick={() => setShowInvite(true)}>{t("inviteButton")}</Button>}
      </div>

      <Tabs
        tabs={tabs.map((tb) => ({ ...tb, label: t(tb.labelKey) }))}
        active={tab}
        onChange={setTab}
      />

      {tab === "activityLog" ? (
        <SubAccountActivityLog />
      ) : loading ? (
        <Loader />
      ) : subAccounts.length === 0 ? (
        <EmptyState icon={Users} text={t("empty")} />
      ) : (
        <div style={s.list}>
          {subAccounts.map((sa) => (
            <Card key={sa.SubAccountId} style={s.card}>
              <div style={s.row}>
                <div style={s.avatarFallback}>{(sa.Name || "?").charAt(0).toUpperCase()}</div>
                <div style={s.info}>
                  <div style={s.nameRow}>
                    <span style={s.name}>{sa.Name}</span>
                    <Badge variant={STATUS_VARIANT[sa.Status] || "neutral"}>{t(`status.${sa.Status}`, sa.Status)}</Badge>
                  </div>
                  <span style={s.email}><Mail size={12} strokeWidth={2} /> {sa.Email}</span>
                  <span style={s.dateLine}>
                    {sa.ActivatedOn ? t("activatedOn", { date: formatDate(sa.ActivatedOn) }) : t("invitedOn", { date: formatDate(sa.CreatedOn) })}
                  </span>
                </div>
              </div>

              <div style={s.actions}>
                <button type="button" className="btn btn-secondary btn-sm" style={s.actionBtn} onClick={() => setEditingPermissions(sa)}>
                  <Shield size={13} strokeWidth={2.25} /> {t("actions.editPermissions")}
                </button>
                {sa.Status === "Pending" && (
                  <button type="button" className="btn btn-secondary btn-sm" style={s.actionBtn} onClick={() => setConfirmAction({ type: "resend", subAccount: sa })}>
                    <RotateCcw size={13} strokeWidth={2.25} /> {t("actions.resendInvite")}
                  </button>
                )}
                {sa.Status === "Active" && (
                  <button type="button" className="btn btn-secondary btn-sm" style={s.actionBtn} onClick={() => setConfirmAction({ type: "block", subAccount: sa })}>
                    <Ban size={13} strokeWidth={2.25} /> {t("actions.block")}
                  </button>
                )}
                {sa.Status === "Blocked" && (
                  <button type="button" className="btn btn-secondary btn-sm" style={s.actionBtn} onClick={() => setConfirmAction({ type: "unblock", subAccount: sa })}>
                    <RotateCcw size={13} strokeWidth={2.25} /> {t("actions.unblock")}
                  </button>
                )}
                <button type="button" className="btn btn-danger btn-sm" style={s.actionBtn} onClick={() => setConfirmAction({ type: "remove", subAccount: sa })}>
                  <Trash2 size={13} strokeWidth={2.25} /> {t("actions.remove")}
                </button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {showInvite && (
        <InviteSubAccountModal
          onClose={() => setShowInvite(false)}
          onInvited={() => { setShowInvite(false); load(); }}
        />
      )}

      {editingPermissions && (
        <EditSubAccountPermissionsModal
          subAccount={editingPermissions}
          onClose={() => setEditingPermissions(null)}
          onUpdated={() => { setEditingPermissions(null); load(); }}
        />
      )}

      {confirmAction && confirmCopy && (
        <ConfirmModal
          title={confirmCopy.title}
          message={confirmCopy.message}
          confirmText={confirmCopy.confirmText}
          danger={confirmCopy.danger}
          loading={actionBusy}
          onConfirm={runConfirmedAction}
          onCancel={() => setConfirmAction(null)}
        />
      )}
    </div>
  );
};

const s = {
  headerRow: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, marginBottom: 20 },
  heading: { margin: "0 0 4px", fontSize: 21, fontWeight: 800, color: color.textPrimary, letterSpacing: -0.4 },
  subheading: { margin: 0, fontSize: 13, color: color.textMuted, maxWidth: 480 },
  list: { display: "flex", flexDirection: "column", gap: 10, maxWidth: 680 },
  card: { padding: "16px 18px" },
  row: { display: "flex", alignItems: "flex-start", gap: 12 },
  avatarFallback: {
    width: 40, height: 40, borderRadius: "50%", background: color.primary50, color: color.primary600,
    display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 15, flexShrink: 0,
  },
  info: { flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 3 },
  nameRow: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
  name: { fontSize: 14, fontWeight: 700, color: color.textPrimary },
  email: { display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12.5, color: color.textSecondary },
  dateLine: { fontSize: 11.5, color: color.textMuted },
  actions: { display: "flex", flexWrap: "wrap", gap: 8, marginTop: 12, paddingTop: 12, borderTop: `1px solid ${color.gray100}` },
  actionBtn: { display: "inline-flex", alignItems: "center", gap: 5, borderRadius: radius.sm },
};

export default SubAccountsPage;
